import { Link } from "react-router-dom";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { testimonials } from "@/data/products";
import { useProducts } from "@/contexts/ProductsContext";
import ProductCard from "@/components/ProductCard";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import NileWaveDivider from "@/components/NileWaveDivider";
import GoldAccentLine from "@/components/GoldAccentLine";
import SEOHead from "@/components/SEOHead";
import { useDiscountBar } from "@/components/StickyDiscountBar";
// import CustomerReviews from "@/components/CustomerReviews"; // disabled: awaiting verified reviews
import FlashDealSection from "@/components/FlashDealSection";
import LazyImage from "@/components/LazyImage";
import Picture from "@/components/Picture";
import { useLanguage } from "@/i18n/LanguageContext";
import { useSiteContent } from "@/hooks/useSiteContent";
import { useState, useEffect, useCallback } from "react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ArrowRight, ArrowLeft, Leaf, FlaskConical, Sparkles, Crown, ChevronLeft, ChevronRight, Truck, ShieldCheck, MapPin, Star, Banknote } from "lucide-react";
import nileImage from "@/assets/nile-section.webp";
import soapImg from "@/assets/tomato-soap-heart.webp";
import moroccanImg from "@/assets/moroccan-soap.webp";
import lipImg from "@/assets/lip-gloss.webp";
import toothpasteImg from "@/assets/toothpaste.webp";
import bodyCareImg from "@/assets/body-butter-100ml.webp";
import hairCareImg from "@/assets/dandruff-shampoo.webp";
import { isOfferActive, calcDiscountedPrice } from "@/utils/offerUtils";
import { optimizeImageUrl, buildSrcSet } from "@/utils/imageTransform";
import CountdownTimer from "@/components/CountdownTimer";
import { productPath } from "@/utils/shareLinks";


const Section = ({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) => {
 const { ref, isVisible } = useScrollReveal();
 return (
 <div ref={ref} className={`transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
 {children}
 </div>
 );
};

type CMSSection = {
 section_key: string;
 is_visible: boolean;
 title: string | null;
 title_ar: string | null;
 subtitle: string | null;
 subtitle_ar: string | null;
 content: any;
};

const Index = () => {
 const [email, setEmail] = useState("");
 const [testimonialIndex, setTestimonialIndex] = useState(0);
 const [cms, setCms] = useState<Record<string, CMSSection>>({});
 const { t, isAr } = useLanguage();
 const { text: scText } = useSiteContent();
 const { products } = useProducts();
 const { visible: discountBarVisible } = useDiscountBar();
 const featuredFlagged = products.filter((p) => p.isFeatured || p.sizeVariants?.some((v) => v.isFeatured));
 const featuredProducts = (featuredFlagged.length > 0 ? featuredFlagged : products.filter((p) => p.badge))
 .slice(0, 4)
 .map((p) => {
 const featuredVariant = p.sizeVariants?.find((v) => v.isFeatured);
 if (!featuredVariant) return p;
 return {
 ...p,
 // Image stays as the product main, featured variant only influences price/offer/stock.
 image: p.image,
 price: featuredVariant.price ?? p.price,
 offer: featuredVariant.offer ?? p.offer,
 stockQuantity: featuredVariant.stockQuantity ?? p.stockQuantity,
 stockStatus: featuredVariant.stockStatus ?? p.stockStatus,
 sizeVariants: undefined,
 };
 });
 const ArrowIcon = isAr ? ArrowLeft : ArrowRight;

 // Hero carousel: rotate through ALL products with unique images, reshuffled every cycle
 const shuffleArray = <T, >(arr: T[]): T[] => {
 const a = arr.slice();
 for (let i = a.length - 1; i > 0; i--) {
 const j = Math.floor(Math.random() * (i + 1));
 [a[i], a[j]] = [a[j], a[i]];
 }
 return a;
 };

 const buildHeroPool = useCallback(() => {
 if (!products.length) return [] as typeof products;
 const seen = new Set<string>();
 const list: typeof products = [];
 for (const p of products) {
 if (!p.image) continue;
 if (!seen.has(p.image)) {
 seen.add(p.image);
 list.push(p);
 }
 }
 return list;
 }, [products]);

 const buildInitialRotation = useCallback(() => {
 const pool = buildHeroPool();
 if (pool.length === 0) return pool;
 const offerP = pool.find((p) => isOfferActive(p.offer));
 const bestsellerP = pool.find((p) => p.id === "tomato-soap") || pool[0];
 const head = offerP || bestsellerP;
 const rest = shuffleArray(pool.filter((p) => p !== head));
 return head ? [head, ...rest] : rest;
 }, [buildHeroPool]);

 const [heroRotation, setHeroRotation] = useState<typeof products>(() => buildInitialRotation());
 const [heroIdx, setHeroIdx] = useState(0);
 const [heroFading, setHeroFading] = useState(false);

 // Rebuild rotation if the product list itself changes
 useEffect(() => {
 setHeroRotation(buildInitialRotation());
 setHeroIdx(0);
 }, [buildInitialRotation]);

 const advanceHero = useCallback(() => {
 setHeroRotation((current) => {
 if (current.length <= 1) return current;
 setHeroFading(true);
 setTimeout(() => {
 setHeroIdx((prev) => {
 const next = prev + 1;
 if (next >= current.length) {
 // Completed a full cycle, reshuffle the whole pool for the next pass
 setHeroRotation(shuffleArray(buildHeroPool()));
 return 0;
 }
 return next;
 });
 setHeroFading(false);
 }, 400);
 return current;
 });
 }, [buildHeroPool]);

 useEffect(() => {
 if (heroRotation.length <= 1) return;
 let intervalId: ReturnType<typeof setInterval> | null = null;
 // Delay the first rotation so the LCP candidate stays stable for browser scoring
 const initialDelay = setTimeout(() => {
 advanceHero();
 intervalId = setInterval(advanceHero, 6000);
 }, 4500);
 return () => {
 clearTimeout(initialDelay);
 if (intervalId) clearInterval(intervalId);
 };
  }, [advanceHero, heroRotation.length]);

  // Once the homepage is idle, warm the /product/:id route and prefetch the
  // hero-sized images for the first product row so tapping any of those cards
  // navigates instantly instead of waiting on chunk + image round-trips.
  useEffect(() => {
    const firstRow = featuredProducts.slice(0, 4);
    if (firstRow.length === 0) return;

    const win = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
      cancelIdleCallback?: (handle: number) => void;
    };
    const schedule = (cb: () => void) =>
      win.requestIdleCallback
        ? win.requestIdleCallback(cb, { timeout: 2500 })
        : (window.setTimeout(cb, 1200) as unknown as number);
    const cancel = (id: number) =>
      win.cancelIdleCallback ? win.cancelIdleCallback(id) : window.clearTimeout(id);

    const handle = schedule(() => {
      // Warm the lazy-loaded ProductPage route chunk.
      import("./ProductPage").catch(() => {});

      // Prefetch each card's product-detail hero at the size the PDP renders.
      // <link rel="prefetch"> so the browser treats them as low-priority and
      // won't contend with anything the user is actively interacting with.
      firstRow.forEach((p) => {
        if (!p.image) return;
        const href = optimizeImageUrl(p.image, { width: 1200, quality: 85 });
        // Avoid duplicating an existing prefetch hint
        if (document.head.querySelector(`link[rel="prefetch"][href="${href}"]`)) return;
        const link = document.createElement("link");
        link.rel = "prefetch";
        link.as = "image";
        link.href = href;
        link.fetchPriority = "low";
        document.head.appendChild(link);
      });
    });

    return () => cancel(handle);
  }, [featuredProducts]);




 const heroPreloadedRef = useState({ done: false })[0];

 const heroProduct = heroRotation[heroIdx] || null;
 const heroOfferActive = heroProduct ? isOfferActive(heroProduct.offer) : false;
 const heroDisplayPrice = heroProduct
 ? heroOfferActive
 ? calcDiscountedPrice(heroProduct.price, heroProduct.offer)
 : heroProduct.price
 : 0;
 const heroImage = heroProduct?.image || soapImg;
 const heroProductName = heroProduct ? (isAr ? heroProduct.nameAr : heroProduct.name) : "";
  const heroProductLink = heroProduct ? productPath(heroProduct.id) : "/shop";

 // Preload the LCP hero image as early as possible. We advertise both an
 // AVIF and a WebP candidate: the browser downloads only the first one
 // whose `type` it can decode, so we get AVIF on modern engines and WebP
 // everywhere else without paying for both.
 useEffect(() => {
 if (heroPreloadedRef.done || !heroImage) return;
 const widths = [400, 600, 800, 1200];
 const sizes = "(max-width: 1024px) 90vw, 480px";
 const makeLink = (format: "avif" | "webp") => {
 const link = document.createElement("link");
 link.rel = "preload";
 link.as = "image";
 link.type = `image/${format}`;
 link.href = optimizeImageUrl(heroImage, { width: 800, quality: 80, format });
 link.setAttribute("fetchpriority", "high");
 const srcset = buildSrcSet(heroImage, widths, 80, format);
 if (srcset) {
 link.setAttribute("imagesrcset", srcset);
 link.setAttribute("imagesizes", sizes);
 }
 return link;
 };
 const links = [makeLink("avif"), makeLink("webp")];
 links.forEach((l) => document.head.appendChild(l));
 heroPreloadedRef.done = true;
 return () => { links.forEach((l) => l.remove()); };
 }, [heroImage, heroPreloadedRef]);

 useEffect(() => {
 supabase.from("landing_page_settings").select("*").then(({ data }) => {
 if (data) {
 const map: Record<string, CMSSection> = {};
 data.forEach((s: any) => { map[s.section_key] = s; });
 setCms(map);
 }
 });
 }, []);

 const isVisible = (key: string) => cms[key]?.is_visible !== false;

 const cmsTitle = (key: string, fallback: string) => {
 const s = cms[key]; if (!s) return fallback;
 const v = isAr ? s.title_ar : s.title;
 return v && v.trim() ? v : fallback;
 };
 const cmsSubtitle = (key: string, fallback: string) => {
 const s = cms[key]; if (!s) return fallback;
 const v = isAr ? s.subtitle_ar : s.subtitle;
 return v && v.trim() ? v : fallback;
 };
 const cmsField = (key: string, field: string, fallback: string) => {
 const c = cms[key]?.content || {};
 const v = isAr ? c[`${field}_ar`] : c[field];
 return v && String(v).trim() ? String(v) : fallback;
 };
 const cmsImage = (key: string, field: string, fallback: string) => {
 const v = cms[key]?.content?.[field];
 return v && String(v).trim() ? String(v) : fallback;
 };

 const handleNewsletter = (e: React.FormEvent) => {
 e.preventDefault();
 toast({ title: t("newsletter.success.title"), description: t("newsletter.success.desc") });
 setEmail("");
 };

 const collections = [
 { title: t("collections.soaps"), desc: t("collections.soaps.desc"), image: soapImg, count: 4 },
 { title: t("collections.moroccan"), desc: t("collections.moroccan.desc"), image: moroccanImg, count: 3 },
 { title: t("collections.lip"), desc: t("collections.lip.desc"), image: lipImg, count: 2 },
 { title: t("collections.toothpaste"), desc: t("collections.toothpaste.desc"), image: toothpasteImg, count: 2 },
 { title: t("collections.bodycare"), desc: t("collections.bodycare.desc"), image: bodyCareImg, count: 2 },
 { title: t("collections.haircare"), desc: t("collections.haircare.desc"), image: hairCareImg, count: 2 },
 ];

 const currentTestimonial = testimonials[testimonialIndex];

 const orgSchema = {
 "@context": "https://schema.org",
 "@type": "Organization",
 name: "Nil Beauty",
 url: "https://www.nilbeautyofficial.com",
 logo: "https://www.nilbeautyofficial.com/favicon.png",
 description: "Nil Beauty is a luxury natural skincare brand from Egypt offering herbal soaps, organic body care, natural toothpaste, body butter, and botanical beauty products inspired by ancient Egyptian wisdom. منتجات طبيعية للعناية بالبشرة.",
 sameAs: [
 "https://www.facebook.com/NilBeautyOfficial/",
 "https://www.instagram.com/nilbeautyofficial/",
 ],
 contactPoint: {
 "@type": "ContactPoint",
 telephone: "+201095544676",
 contactType: "customer service",
 email: "info@nilbeautyofficial.com",
 },
 };

 return (
 <div className="min-h-screen bg-background">
 
 <SEOHead
 title="Nil Beauty, Natural Skincare from Egypt"
 description="Premium natural skincare from Egypt: herbal soaps, body butter, lip care, and organic beauty inspired by ancient Egyptian wisdom."
 canonical="https://www.nilbeautyofficial.com/"
 keywords="natural skincare, organic skincare, body butter, herbal soap, skin brightening, beauty Egypt, العناية بالبشرة, منتجات طبيعية, ترطيب البشرة, زبدة الجسم"
 jsonLd={orgSchema}
 />
 <Navbar />
 <main>
 {/* Hero, split layout: storytelling text + product anchor */}
 {isVisible("hero") && (
 <section className="relative bg-gradient-to-br from-muted via-background to-muted overflow-hidden">
 {cms.hero?.content?.background_image && (
 <div className="absolute inset-0 pointer-events-none">
 <img src={optimizeImageUrl(cms.hero.content.background_image, { width: 1280, quality: 35 })} alt="" aria-hidden="true" loading="eager" fetchPriority="high" decoding="async" width={1280} height={720} className="w-full h-full object-cover opacity-20" />
 </div>
 )}
 <div className="absolute top-0 right-0 w-96 h-96 gold-gradient opacity-[0.07] rounded-full blur-3xl pointer-events-none" />
 <div className="absolute bottom-0 left-0 w-80 h-80 gold-gradient opacity-[0.05] rounded-full blur-3xl pointer-events-none" />

 <div className={`container mx-auto px-4 pb-12 md:pb-16 lg:pb-20 relative ${discountBarVisible ? "pt-40 md:pt-36 lg:pt-32" : "pt-24 md:pt-24 lg:pt-24"}`}>
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center min-h-[460px] lg:min-h-[500px]">
 {/* Left: copy + CTA */}
 <div className={`text-center ${isAr ? "lg:text-right" : "lg:text-left"} order-2 lg:order-1 lg:self-center`}>
 {/* Free delivery pill */}
 <div className={`inline-flex items-center gap-2 bg-primary/10 border border-primary/30 px-4 py-1.5 rounded-full mb-5`}>
 <Truck size={14} className="text-primary" strokeWidth={2}  aria-hidden="true" focusable="false" />
 <span className="font-body text-xs uppercase tracking-wider text-primary font-semibold">
 {scText("home.hero.delivery_badge", isAr, t("hero.badge.delivery"))}
 </span>
 </div>

                                                {/* Redundant top trust row + dot-separated value prop removed. Trust signals are consolidated into a single row below the CTA for a cleaner F-pattern scan. */}

  <p className="font-body text-xs sm:text-sm uppercase tracking-[0.35em] text-primary mb-3">
 {scText("home.hero.tagline", isAr, cmsSubtitle("hero", t("hero.tagline")))}
 </p>

 <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-[1.1] mb-4">
 {scText("home.hero.title", isAr, cmsTitle("hero", t("hero.title")))}
 </h1>

 {/* Bilingual value proposition, single concise line above the paragraph */}
 <div className={`mb-3 ${isAr ? "lg:text-right" : "lg:text-left"} text-center`} aria-label="Value proposition">
 {isAr ? (
 <p lang="ar" dir="rtl" className="font-arabic text-sm md:text-[15px] text-foreground/85 leading-snug">
 <span className="text-primary font-semibold">{scText("home.hero.value_prop_highlight", true, "صناعة يدوية في مصر")}</span>
 <span className="text-muted-foreground"> • {scText("home.hero.value_prop_rest", true, "عناية نقية بطقوس ملكية • دفع آمن • شحن لكل المحافظات")}</span>
 </p>
 ) : (
 <p lang="en" dir="ltr" className="font-body text-sm md:text-[15px] text-foreground/85 leading-snug">
 <span className="text-primary font-semibold">{scText("home.hero.value_prop_highlight", false, "Handcrafted in Egypt")}</span>
 <span className="text-muted-foreground"> · {scText("home.hero.value_prop_rest", false, "Clean, ritual-grade skincare · Secure payment, nationwide delivery")}</span>
 </p>
 )}
 </div>

                                <p
                                        dir={isAr ? "rtl" : "ltr"}
                                        lang={isAr ? "ar" : "en"}
                                        className={`${isAr ? "font-arabic" : "font-body"} text-base md:text-lg text-muted-foreground mb-5 max-w-xl mx-auto text-center ${isAr ? "lg:text-right lg:mr-0 lg:ml-auto" : "lg:text-left lg:ml-0 lg:mr-auto"} leading-relaxed`}
                                >
                                        {scText("home.hero.paragraph", isAr, cmsField("hero", "paragraph", t("hero.subtitle")))}
                                </p>

                                {/* Price anchor */}
                                {heroProduct && (
                                        <div className="flex items-baseline justify-center lg:justify-start gap-2 mb-4">
                                                <span className="font-body text-xs uppercase tracking-widest text-muted-foreground">
                                                        {scText("home.hero.price_from_label", isAr, t("hero.from"))}
                                                </span>
                                                <span className="font-serif text-3xl md:text-4xl font-bold text-foreground">
                                                        {heroDisplayPrice} <span className="text-xl">{scText("home.hero.currency", isAr, t("hero.currency"))}</span>
                                                </span>
                                                {heroOfferActive && (
                                                        <span className="font-serif text-lg text-muted-foreground line-through">
                                                                {heroProduct.price} {scText("home.hero.currency", isAr, t("hero.currency"))}
                                                        </span>
                                                )}
                                        </div>
                                )}

                                {/* Shop Now CTA, placed under the price in the empty area */}
                                {heroProduct && (
                                        <div className="flex justify-center lg:justify-start mb-6">
                                                <Link
                                                        to={heroProductLink}
                                                        className="group relative inline-flex items-center gap-3 overflow-hidden rounded-full border border-foreground bg-foreground px-10 py-4 font-body text-sm uppercase tracking-[0.25em] text-background shadow-[0_18px_40px_-18px_rgba(0,0,0,0.55)] transition-all duration-500 hover:shadow-[0_0_28px_rgba(180,140,58,0.45)]"
                                                >
                                                        <span aria-hidden className="pointer-events-none absolute inset-x-0 -bottom-px h-[calc(100%+2px)] gold-gradient translate-y-full transition-transform duration-500 ease-out group-hover:translate-y-0 will-change-transform" />
                                                        <span className="relative z-10 transition-colors duration-500 group-hover:text-foreground">
                                                                {scText("home.hero.cta", isAr, cmsField("hero", "cta_text", t("hero.cta")))}
                                                        </span>
                                                        <ArrowIcon size={16} className={`relative z-10 transition-all duration-500 group-hover:text-foreground ${isAr ? "group-hover:-translate-x-1" : "group-hover:translate-x-1"}`}  aria-hidden="true" focusable="false" />
                                                </Link>
                                        </div>
                                )}

                                                {/* Countdown slot, always reserved so content doesn't shift vertically when rotating between products with/without an offer. Invisible placeholder when no offer. */}
                                                <div className={`min-h-[72px] flex items-start ${"lg:justify-start"} justify-center mb-5`} aria-hidden={!(heroProduct?.offer?.endDate && heroOfferActive)}>
                                                        {heroProduct?.offer?.endDate && heroOfferActive && (
                                                                <CountdownTimer endDate={heroProduct.offer.endDate} />
                                                        )}
                                                </div>

 {/* Trust signals, side by side */}
 <div className={`flex flex-wrap items-center justify-center lg:justify-start gap-x-2 gap-y-2 text-sm text-foreground/85 mt-6 md:mt-8`}>
 <span className="inline-flex items-center gap-1.5 bg-foreground/[0.04] border border-primary/30 px-3 py-1.5 rounded-full">
 <span className="flex items-center gap-0.5" aria-hidden="true">
 {[0, 1, 2, 3, 4].map((i) => (
 <Star key={i} size={12} className="text-primary fill-primary" strokeWidth={0}  aria-hidden="true" focusable="false" />
 ))}
 </span>
 <span className="font-body text-xs font-semibold text-foreground">
 {scText("home.hero.rating_value", isAr, isAr ? "٤٫٩" : "4.9")}
 </span>
 <span className="font-body text-[11px] text-muted-foreground">
 {scText("home.hero.rating_count", isAr, isAr ? "(+٥٠٠)" : "(500+)")}
 </span>
 </span>
 <span className="inline-flex items-center gap-1.5 bg-foreground/[0.04] border border-primary/20 px-3 py-1.5 rounded-full">
 <ShieldCheck size={14} className="text-primary shrink-0" strokeWidth={2}  aria-hidden="true" focusable="false" />
 <span className="font-body text-xs font-medium whitespace-nowrap">{scText("home.hero.trust_cod", isAr, t("hero.trust.cod"))}</span>
 </span>
 <span className="inline-flex items-center gap-1.5 bg-foreground/[0.04] border border-primary/20 px-3 py-1.5 rounded-full">
 <Leaf size={14} className="text-primary shrink-0" strokeWidth={2}  aria-hidden="true" focusable="false" />
 <span className="font-body text-xs font-medium whitespace-nowrap">{scText("home.hero.trust_natural", isAr, t("hero.trust.natural"))}</span>
 </span>
 <span className="inline-flex items-center gap-1.5 bg-foreground/[0.04] border border-primary/20 px-3 py-1.5 rounded-full">
 <MapPin size={14} className="text-primary shrink-0" strokeWidth={2}  aria-hidden="true" focusable="false" />
 <span className="font-body text-xs font-medium whitespace-nowrap">{scText("home.hero.trust_egypt", isAr, t("hero.trust.egypt"))}</span>
 </span>
 </div>
 </div>

 {/* Right: hero product photo, auto-rotating */}
 <div className="order-1 lg:order-2 relative">
 <Link to={heroProductLink} className="block group" aria-label={heroProductName}>
 <div className="relative aspect-square max-w-md mx-auto">
 {/* Gold backdrop accent */}
 <div className={`absolute inset-4 gold-gradient opacity-15 rounded-sm transform ${isAr ? "-rotate-3" : "rotate-3"} transition-transform group-hover:rotate-0 duration-700`} />
   <Picture
   key={heroImage}
   src={heroImage}
   widths={[400, 600, 800, 1200]}
   displayWidth={800}
   quality={80}
   sizes="(max-width: 1024px) 90vw, 480px"
   alt={heroProductName ? `${heroProductName}, Nil Beauty natural skincare` : "Nil Beauty natural skincare hero product"}
   title={heroProductName}
   className={`relative w-full h-full object-cover rounded-sm transition-all duration-700 group-hover:scale-[1.02] ${heroFading ? "opacity-0 scale-95" : "opacity-100 scale-100"}`}
   width={600}
   height={600}
   loading="eager"
   fetchPriority="high"
   decoding="async"
   withSkeleton
   wrapperClassName="absolute inset-0 rounded-sm shadow-2xl"
   />

 {/* Floating badges */}
 {heroOfferActive ? (
 <div className="absolute top-4 left-4 gold-gradient text-foreground font-body text-xs uppercase tracking-widest px-3 py-1.5 rounded-sm shadow-md">
 {scText("home.hero.offer_badge", isAr, t("hero.badge.offer"))}
 </div>
 ) : (
 <div className="absolute top-4 left-4 bg-foreground text-background font-body text-xs uppercase tracking-widest px-3 py-1.5 rounded-sm shadow-md">
 {scText("home.hero.bestseller_badge", isAr, `★ ${t("hero.bestseller")}`)}
 </div>
 )}
 {/* Product name label */}
 <div className={`absolute bottom-4 ${isAr ? "right-4" : "left-4"} bg-background/80 backdrop-blur-sm text-foreground font-serif text-sm px-3 py-1.5 rounded-sm shadow transition-opacity duration-500 ${heroFading ? "opacity-0" : "opacity-100"}`}>
 {heroProductName}
 </div>
 </div>
 </Link>
 {/* Dot indicators */}
 {heroRotation.length > 1 && (
 <div className="flex items-center justify-center gap-2 mt-4">
 {heroRotation.map((_, i) => (
 <button
 key={i}
 onClick={() => { setHeroFading(true); setTimeout(() => { setHeroIdx(i); setHeroFading(false); }, 400); }}
 className={`w-2 h-2 rounded-full transition-all duration-300 ${i === heroIdx ? "bg-primary w-5" : "bg-muted-foreground/30 hover:bg-muted-foreground/60"}`}
 aria-label={`Show product ${i + 1}`}
 />
 ))}
 </div>
 )}
 </div>
 </div>
 </div>
 </section>
 )}

 <NileWaveDivider />

 {/* Brand Promise Strip */}
 {isVisible("brand_promise") && (
 <Section>
 <div className="bg-foreground py-16 relative overflow-hidden">
 {cmsImage("brand_promise", "background_image", "") && (
 <div className="absolute inset-0 z-0">
 <img src={optimizeImageUrl(cmsImage("brand_promise", "background_image", ""), { width: 1280, quality: 40 })} alt="" aria-hidden="true" loading="lazy" decoding="async" width={1280} height={720} className="w-full h-full object-cover opacity-30" />
 <div className="absolute inset-0 bg-foreground/70" />
 </div>
 )}
 <div className="container mx-auto px-4 grid grid-cols-1 md:grid-cols-3 gap-8 text-center relative z-10">
 {[
 { icon: Leaf, title: t("promise.natural.title"), desc: t("promise.natural.desc") },
 { icon: Crown, title: t("promise.wisdom.title"), desc: t("promise.wisdom.desc") },
 { icon: FlaskConical, title: t("promise.science.title"), desc: t("promise.science.desc") },
 ].map((item, i) => (
 <div key={i} className="flex flex-col items-center gap-4">
 <item.icon className="text-primary" size={28} strokeWidth={1.5} />
 <h2 className="font-serif text-lg text-background">{item.title}</h2>
 <p className="font-body text-sm text-background/50">{item.desc}</p>
 </div>
 ))}
 </div>
 </div>
 </Section>
 )}

 {/* Collections */}
 {isVisible("collections") && (
 <section className="py-24 relative overflow-hidden">
 {cmsImage("collections", "background_image", "") && (
 <div className="absolute inset-0 z-0">
 <img src={optimizeImageUrl(cmsImage("collections", "background_image", ""), { width: 1280, quality: 40 })} alt="" aria-hidden="true" loading="lazy" decoding="async" width={1280} height={720} className="w-full h-full object-cover opacity-15" />
 <div className="absolute inset-0 bg-background/70" />
 </div>
 )}
 <div className="container mx-auto px-4 relative z-10">
 <Section>
 <div className="text-center mb-16">
 <p className="font-body text-sm uppercase tracking-[0.3em] text-primary mb-3">{cmsSubtitle("collections", t("collections.tagline"))}</p>
 <h2 className="font-serif text-3xl md:text-5xl font-bold">{cmsTitle("collections", t("collections.title"))}</h2>
 <GoldAccentLine className="mt-4" />
 </div>
 </Section>
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
 {collections.map((col, i) => (
 <Section key={i} delay={i * 100}>
 <Link to="/shop" className="group relative block aspect-[4/5] overflow-hidden rounded-sm">
 <LazyImage src={col.image} alt={`${col.title}, Nil Beauty natural ${col.title.toLowerCase()} collection for organic skincare`} title={`Nil Beauty ${col.title}`} wrapperClassName="absolute inset-0 w-full h-full" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
 <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-transparent" />
 <div className="absolute bottom-0 left-0 right-0 p-6">
 <h3 className="font-serif text-xl text-background mb-1">{col.title}</h3>
 <p className="font-body text-sm text-background/60">{col.desc}</p>
 <p className="font-body text-xs text-primary mt-2 uppercase tracking-widest">{col.count} {t("collections.products")}</p>
 </div>
 </Link>
 </Section>
 ))}
 </div>
 <Section>
 <div className="text-center mt-14">
 <Link
 to="/shop"
 className="group relative inline-flex items-center gap-3 overflow-hidden rounded-full border border-primary/40 bg-gradient-to-r from-[#8a6d2b] via-primary to-[#8a6d2b] bg-[length:200%_100%] px-9 py-3.5 font-body text-sm uppercase tracking-[0.25em] text-primary-foreground shadow-[0_10px_30px_-12px_hsl(var(--primary)/0.55)] transition-all duration-500 hover:bg-[position:100%_0] hover:shadow-[0_14px_40px_-12px_hsl(var(--primary)/0.75)] hover:scale-[1.03]"
 >
 {/* Shine sweep */}
 <span
 aria-hidden
 className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 -skew-x-12 bg-gradient-to-r from-transparent via-white/40 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-[300%]"
 />
 <span className="relative z-10">
 {isAr ? "تصفح كل المجموعات" : "Explore All Collections"}
 </span>
 <ArrowIcon
 size={16}
 className={`relative z-10 transition-transform duration-300 ${isAr ? "group-hover:-translate-x-1" : "group-hover:translate-x-1"}`}  aria-hidden="true" focusable="false" />
 </Link>
 </div>
 </Section>
 </div>
 </section>
 )}

 {/* Flash Deal (auto-rotates every 2 days) */}
 <FlashDealSection />

 {/* Customer Reviews, disabled until verified-purchase reviews are collected */}
 {/* {isVisible("best_sellers") && <CustomerReviews />} */}

 <NileWaveDivider />

 {/* Born From The Nile */}
 {isVisible("nile_story") && (
 <Section>
 <section className="py-24 bg-muted relative overflow-hidden">
 {cmsImage("nile_story", "background_image", "") && (
 <div className="absolute inset-0 z-0">
 <img src={optimizeImageUrl(cmsImage("nile_story", "background_image", ""), { width: 1280, quality: 40 })} alt="" aria-hidden="true" loading="lazy" decoding="async" width={1280} height={720} className="w-full h-full object-cover opacity-20" />
 <div className="absolute inset-0 bg-muted/60" />
 </div>
 )}
 <div className="container mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">
                                                <div className="relative">
                                                        <div aria-hidden className={`absolute -inset-5 lg:-inset-6 border border-primary/30 pointer-events-none ${isAr ? "translate-x-2" : "-translate-x-2"} translate-y-2`} />
                                                        <LazyImage
                                                                src={cmsImage("nile_story", "image_url", nileImage)}
                                                                alt="Nil Beauty heritage, natural skincare ingredients inspired by the ancient Nile River and Egyptian beauty traditions"
                                                                title="Born From The Nile, Nil Beauty Heritage"
                                                                className="relative w-full h-[500px] object-cover rounded-sm shadow-2xl"
                                                        />
                                                        <div className={`absolute -bottom-6 ${isAr ? "-left-6" : "-right-6"} w-32 h-32 gold-gradient opacity-25 rounded-sm hidden lg:block`} />
                                                </div>
 <div>
 <p className="font-body text-sm uppercase tracking-[0.3em] text-primary mb-4">{cmsSubtitle("nile_story", t("nile.tagline"))}</p>
 <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6">{cmsTitle("nile_story", t("nile.title"))}</h2>
 <GoldAccentLine className="mb-6 justify-start" />
 <p className="font-body text-muted-foreground leading-relaxed mb-6 whitespace-pre-line">{cmsField("nile_story", "paragraph", `${t("nile.p1")}\n\n${t("nile.p2")}`)}</p>
 <Link to={cmsField("nile_story", "cta_link", "/about")} className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-widest text-primary hover:opacity-70 transition-opacity">
 {cmsField("nile_story", "cta_text", t("nile.cta"))} <ArrowIcon size={14}  aria-hidden="true" focusable="false" />
 </Link>
 </div>
 </div>
 </section>
 </Section>
 )}

 {/* Why Nil Beauty */}
 {isVisible("why_nil") && (
 <section className="py-24 relative overflow-hidden">
 {cmsImage("why_nil", "background_image", "") && (
 <div className="absolute inset-0 z-0">
 <img src={optimizeImageUrl(cmsImage("why_nil", "background_image", ""), { width: 1280, quality: 40 })} alt="" aria-hidden="true" loading="lazy" decoding="async" width={1280} height={720} className="w-full h-full object-cover opacity-15" />
 <div className="absolute inset-0 bg-background/70" />
 </div>
 )}
 <div className="container mx-auto px-4 relative z-10">
 <Section>
 <div className="text-center mb-16">
 <p className="font-body text-sm uppercase tracking-[0.3em] text-primary mb-3">{cmsSubtitle("why_nil", t("why.tagline"))}</p>
 <h2 className="font-serif text-3xl md:text-5xl font-bold">{cmsTitle("why_nil", t("why.title"))}</h2>
 <GoldAccentLine className="mt-4" />
 </div>
 </Section>
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
 {[
 { icon: Leaf, title: t("why.natural.title"), desc: t("why.natural.desc") },
 { icon: Crown, title: t("why.heritage.title"), desc: t("why.heritage.desc") },
 { icon: FlaskConical, title: t("why.tested.title"), desc: t("why.tested.desc") },
 { icon: Sparkles, title: t("why.results.title"), desc: t("why.results.desc") },
 ].map((item, i) => (
 <Section key={i} delay={i * 100}>
 <div className="group text-center p-8 border border-border rounded-sm hover:border-primary hover:-translate-y-1 hover:shadow-[0_24px_50px_-24px_rgba(180,140,58,0.45)] transition-all duration-500 bg-background/60 backdrop-blur-sm">
 <item.icon className="mx-auto text-primary mb-4" size={32} strokeWidth={1.5} />
 <h3 className="font-serif text-lg font-semibold mb-2">{item.title}</h3>
 <p className="font-body text-sm text-muted-foreground">{item.desc}</p>
 </div>
 </Section>
 ))}
 </div>
 </div>
 </section>
 )}

 {/* Promotional Banner */}
 {isVisible("promo_banner") && (cmsTitle("promo_banner", "") || cmsImage("promo_banner", "image_url", "") || cmsImage("promo_banner", "background_image", "")) && (
 <Section>
 <section className="relative py-20 overflow-hidden bg-foreground">
 {cmsImage("promo_banner", "background_image", "") && (
 <div className="absolute inset-0 z-0">
 <img src={optimizeImageUrl(cmsImage("promo_banner", "background_image", ""), { width: 1280, quality: 40 })} alt="" aria-hidden="true" loading="lazy" decoding="async" width={1280} height={720} className="w-full h-full object-cover opacity-40" />
 <div className="absolute inset-0 bg-foreground/60" />
 </div>
 )}
 <div className="container mx-auto px-4 relative z-10 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
 {cmsImage("promo_banner", "image_url", "") && (
 <div className="flex justify-center">
 <img src={optimizeImageUrl(cmsImage("promo_banner", "image_url", ""), { width: 360, quality: 70 })} alt="" aria-hidden="true" loading="lazy" decoding="async" width={324} height={576} className="max-h-72 object-contain" style={{ width: "auto", height: "auto", maxHeight: "18rem" }} />
 </div>
 )}
 <div className={cmsImage("promo_banner", "image_url", "") ? "text-center md:text-start" : "text-center md:col-span-2"}>
 {cmsSubtitle("promo_banner", "") && (
 <p className="font-body text-sm uppercase tracking-[0.3em] text-primary mb-3">{cmsSubtitle("promo_banner", "")}</p>
 )}
 {cmsTitle("promo_banner", "") && (
 <h2 className="font-serif text-3xl md:text-5xl font-bold text-background mb-4">{cmsTitle("promo_banner", "")}</h2>
 )}
 {cmsField("promo_banner", "paragraph", "") && (
 <p className="font-body text-background/80 mb-6 whitespace-pre-line">{cmsField("promo_banner", "paragraph", "")}</p>
 )}
 {cmsField("promo_banner", "cta_text", "") && (
 <Link to={cmsField("promo_banner", "cta_link", "/shop")} className="inline-flex items-center gap-2 gold-gradient text-foreground font-body text-sm uppercase tracking-widest px-8 py-3 hover:opacity-90 transition-opacity rounded-full">
 {cmsField("promo_banner", "cta_text", "")} <ArrowIcon size={14}  aria-hidden="true" focusable="false" />
 </Link>
 )}
 </div>
 </div>
 </section>
 </Section>
 )}

 {/* Featured Products */}
 {isVisible("featured_products") && (
 <Section>
 <section className="relative py-24 bg-muted overflow-hidden">
 {cmsImage("featured_products", "background_image", "") && (
 <div className="absolute inset-0 z-0">
 <img src={optimizeImageUrl(cmsImage("featured_products", "background_image", ""), { width: 1280, quality: 40 })} alt="" aria-hidden="true" loading="lazy" decoding="async" width={1280} height={720} className="w-full h-full object-cover opacity-20" />
 <div className="absolute inset-0 bg-muted/60" />
 </div>
 )}
 <div className="container mx-auto px-4 relative z-10">
 <div className="text-center mb-16">
 {cmsImage("featured_products", "image_url", "") && (
 <img src={optimizeImageUrl(cmsImage("featured_products", "image_url", ""), { width: 400, quality: 70 })} alt="" aria-hidden="true" loading="lazy" decoding="async" width={400} height={200} className="mx-auto mb-6 max-h-40 object-contain" style={{ width: "auto", height: "auto" }} />
 )}
 <p className="font-body text-sm uppercase tracking-[0.3em] text-primary mb-3">{cmsSubtitle("featured_products", t("featured.tagline"))}</p>
 <h2 className="font-serif text-3xl md:text-5xl font-bold">{cmsTitle("featured_products", t("featured.title"))}</h2>
 </div>
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-start">
  {featuredProducts.map((product, i) => (
   // First row (4 on desktop, 2 on tablet) loads eagerly so it
   // contributes to LCP without waiting for the IntersectionObserver.
   <ProductCard
    key={product.id}
    product={product}
    priority={i < 4}
    prefetchNext={featuredProducts[i + 1]?.image}
   />
  ))}
 </div>
 <div className="text-center mt-12">
 <Link to={cmsField("featured_products", "cta_link", "/shop")} className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-widest text-primary hover:opacity-70 transition-opacity">
 {cmsField("featured_products", "cta_text", t("featured.viewall"))} <ArrowIcon size={14}  aria-hidden="true" focusable="false" />
 </Link>
 </div>
 </div>
 </section>
 </Section>
 )}

 <NileWaveDivider />

 {/* Testimonials */}
 {isVisible("testimonials") && testimonials.length > 0 && (
 <Section>
 <section className="py-24 bg-foreground relative overflow-hidden">
 {cmsImage("testimonials", "background_image", "") && (
 <div className="absolute inset-0 z-0">
 <img src={optimizeImageUrl(cmsImage("testimonials", "background_image", ""), { width: 1280, quality: 40 })} alt="" aria-hidden="true" loading="lazy" decoding="async" width={1280} height={720} className="w-full h-full object-cover opacity-25" />
 <div className="absolute inset-0 bg-foreground/70" />
 </div>
 )}
 <div className="relative z-10">
 <div className="container mx-auto px-4 text-center">
 <p className="font-body text-sm uppercase tracking-[0.3em] text-primary mb-3">{cmsSubtitle("testimonials", t("testimonials.tagline"))}</p>
 <h2 className="font-serif text-3xl md:text-4xl font-bold text-background mb-16">{cmsTitle("testimonials", t("testimonials.title"))}</h2>
 <div className="max-w-2xl mx-auto relative">
 <blockquote className="font-serif text-xl md:text-2xl text-background/90 italic leading-relaxed mb-8">
 "{isAr ? currentTestimonial.textAr : currentTestimonial.text}"
 </blockquote>
 <p className="font-body text-sm text-primary uppercase tracking-widest">{isAr ? currentTestimonial.nameAr : currentTestimonial.name}</p>
 <p className="font-body text-xs text-background/40 mt-1">{isAr ? currentTestimonial.locationAr : currentTestimonial.location}</p>
 <div className="flex items-center justify-center gap-4 mt-10">
 <button onClick={() => setTestimonialIndex((i) => (i === 0 ? testimonials.length - 1 : i - 1))} className="text-background/40 hover:text-primary transition-colors" aria-label="Previous">
 <ChevronLeft size={20}  aria-hidden="true" focusable="false" />
 </button>
 <div className="flex gap-2">
 {testimonials.map((_, i) => (
 <button key={i} onClick={() => setTestimonialIndex(i)} className={`w-2 h-2 rounded-full transition-colors ${i === testimonialIndex ? "bg-primary" : "bg-background/20"}`} aria-label={`Testimonial ${i + 1}`} />
 ))}
 </div>
 <button onClick={() => setTestimonialIndex((i) => (i === testimonials.length - 1 ? 0 : i + 1))} className="text-background/40 hover:text-primary transition-colors" aria-label="Next">
 <ChevronRight size={20}  aria-hidden="true" focusable="false" />
 </button>
 </div>
 </div>
 </div>
 </div>
 </section>
 </Section>
 )}

 {/* Newsletter */}
 {isVisible("newsletter") && (
 <Section>
 <section className="py-24 relative overflow-hidden">
 {cmsImage("newsletter", "background_image", "") && (
 <div className="absolute inset-0 z-0">
 <img src={optimizeImageUrl(cmsImage("newsletter", "background_image", ""), { width: 1280, quality: 40 })} alt="" aria-hidden="true" loading="lazy" decoding="async" width={1280} height={720} className="w-full h-full object-cover opacity-15" />
 <div className="absolute inset-0 bg-background/70" />
 </div>
 )}
 <div className="relative z-10">
 <div className="container mx-auto px-4 text-center max-w-xl">
 <p className="font-body text-sm uppercase tracking-[0.3em] text-primary mb-3">{cmsSubtitle("newsletter", t("newsletter.tagline"))}</p>
 <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">{cmsTitle("newsletter", t("newsletter.title"))}</h2>
 <p className="font-body text-muted-foreground mb-8">{cmsField("newsletter", "paragraph", t("newsletter.desc"))}</p>
 <form onSubmit={handleNewsletter} className="flex flex-col sm:flex-row gap-3">
 <input
 type="email"
 required
 value={email}
 onChange={(e) => setEmail(e.target.value)}
 placeholder={t("newsletter.placeholder")}
                  className="flex-1 bg-transparent border border-primary/30 px-5 py-3 font-body text-sm focus:outline-none focus:border-primary transition-colors placeholder:text-muted-foreground rounded-full"
                />
                <button type="submit" className="gold-gradient text-foreground font-body text-sm uppercase tracking-widest px-8 py-3 hover:opacity-90 transition-opacity whitespace-nowrap rounded-full">
 {t("newsletter.button")}
 </button>
 </form>
 <p className="font-body text-xs text-muted-foreground/80 mt-4 leading-relaxed">
 {t("language") === "ar"
 ? "بالاشتراك بتوافق إننا نستخدم بريدك للرد على طلبك، إرسال التحديثات، وتحسين تجربتك. مش بنبيع معلوماتك لأي طرف تالت. "
 : "By subscribing, you agree that we may use your email to respond to your request, send updates, and improve your experience. We do not sell personal information to third parties. "}
 <a href="/privacy" className="underline hover:text-primary">
 {t("language") === "ar" ? "سياسة الخصوصية" : "Privacy Policy"}
 </a>
 </p>
 </div>
 </div>
 </section>
 </Section>
 )}

 </main>
 <Footer />
 </div>
 );
};

export default Index;
